import React, { useState, useId, useRef, useEffect } from 'react';
import classnames from 'classnames';
import styles from './combo-button.module.scss';
import { Icon } from '../index';
import { useOutsideClick } from '../../hooks';
import { InteractionElement, InteractionElementType } from './interaction-element';

export type ComboButtonProps = {
  items: InteractionElementType[];
  variant: 'primary' | 'secondary';
  cta: InteractionElementType;
  dropDownAriaLabel: string;
  size?: '200' | '300';
  disabled?: boolean;
  className?: string;
};

export const ComboButton: React.FC<ComboButtonProps> = ({
  className,
  items,
  variant,
  size = '300',
  disabled,
  cta,
  dropDownAriaLabel
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const flyoutButtonRef = useRef<HTMLButtonElement>(null);
  const flyoutListRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const classes = classnames(styles.comboButtonOuter, { [styles.open]: open }, styles[`s${size}`], className);
  const comboButtonClasses = classnames(styles.comboButton, { [styles.disabled]: disabled }, styles[variant]);

  const icon = size === '200' ? 'chevron-compact-down--small' : 'chevron-compact-down--medium';

  const handleClose = () => {
    setOpen(false);
  };

  useOutsideClick(ref, handleClose);

  useEffect(() => {
    if (!open) {
      return;
    }

    document.body.classList.add('no-scroll');

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  useEffect(() => {
    if (!ref.current || !flyoutButtonRef.current || !flyoutListRef.current) {
      return;
    }

    const el = ref.current;
    const flyoutButtonEl = flyoutButtonRef.current;
    const flyoutListEl = flyoutListRef.current;
    const focusableElementList = flyoutListEl.querySelectorAll('a[href]:not([disabled]), button:not([disabled])');

    const firstFocusableElement = focusableElementList[0];
    const lastFocusableElement = focusableElementList[focusableElementList.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          flyoutButtonEl.focus();
          return setOpen(false);
        case 'Tab':
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              return handleClose();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              return handleClose();
            }
          }
      }
    };

    el.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <nav className={classes} ref={ref} data-testid="combo-button-component">
      <div className={comboButtonClasses}>
        <InteractionElement props={cta} />
        <button
          aria-expanded={open}
          aria-haspopup
          aria-label={dropDownAriaLabel}
          onClick={() => setOpen(!open)}
          className={classnames(styles.button, styles.dropdown)}
          disabled={disabled}
          ref={flyoutButtonRef}
        >
          <Icon name={icon} aria-hidden className={styles.chevron} />
        </button>
      </div>
      <div aria-hidden={!open} className={styles.flyout} ref={flyoutListRef}>
        <ul className={styles.list}>
          {items.map((item, i) => (
            <li key={id + i} className={styles.item}>
              <InteractionElement props={item} />
            </li>
          ))}
        </ul>
      </div>
      {open && <div className={styles.layer} />}
    </nav>
  );
};
